// src/lib/api_businesses.ts
// Cliente tipado para /admin/businesses/** (Django Ninja).

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return { Authorization: `Bearer ${await getJwt()}` };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init, headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface Business {
  id: string;
  org_id: string;
  parent_business_id: string | null;
  name: string;
  slug: string;
  description: string;
  logo_url: string;
  website: string;
  primary_color: string;
  secondary_color: string;

  fb_page_id: string;
  fb_page_name: string;
  ig_business_id: string;
  ig_username: string;
  ad_account_id: string;
  ad_account_name: string;
  waba_id: string;
  wa_phone_number_id: string;

  has_meta_connected: boolean;
  has_instagram: boolean;
  has_ads: boolean;

  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BusinessIn {
  name: string;
  slug?: string;
  description?: string;
  logo_url?: string;
  website?: string;
  primary_color?: string;
  secondary_color?: string;
  parent_business_id?: string | null;
}

export interface BusinessUpdate {
  name?: string;
  description?: string;
  logo_url?: string;
  website?: string;
  primary_color?: string;
  secondary_color?: string;
  is_active?: boolean;
  parent_business_id?: string | null;
}

export interface AvailableMetaAccounts {
  connected: boolean;
  pages: {
    id: string; name: string; category: string; tasks: string[];
  }[];
  ad_accounts: {
    id: string; account_id: string; name: string; business_name: string; currency: string;
  }[];
}

export interface SelectMetaAccountsIn {
  fb_page_id: string;
  ad_account_id?: string | null;
}

export interface MetaAccountsOut {
  fb_page_id: string;
  fb_page_name: string;
  ig_business_id: string;
  ig_username: string;
  ad_account_id: string;
  ad_account_name: string;
  has_meta_connected: boolean;
}

export type FrequencyType = 'posts_per_week' | 'interval_days';
export type ClientType = 'services' | 'products' | 'personal';
export type ImageMode = 'ai_generated' | 'ai_enhanced';
export type Language = 'es' | 'en' | 'pt' | 'fr';

export interface ContentConfig {
  id: string;
  business_id: string;
  is_enabled: boolean;
  auto_publish: boolean;
  publish_immediately: boolean;
  frequency_type: FrequencyType;
  posts_per_week: number;
  interval_days: number;
  last_generated_at: string | null;
  paused_until: string | null;
  brand_description: string;
  products_services: string;
  target_audience: string;
  tone_of_voice: string;
  visual_style: string;
  generation_notes: string;
  content_topics: string[];
  client_type: ClientType;
  image_mode: ImageMode;
  language: Language;
  post_to_facebook: boolean;
  post_to_instagram: boolean;
  // Video auto-posting (Fase 5)
  video_enabled?: boolean;
  video_tier?: string;
  content_format?: string;
  stories_per_week?: number;
  reels_per_week?: number;
  version: number;
  updated_at: string;
}

export interface ContentConfigIn extends Partial<Omit<ContentConfig, 'id' | 'business_id' | 'version' | 'updated_at' | 'last_generated_at'>> {}

export interface BusinessEvent {
  id: string;
  kind: string;
  actor: string;
  payload: Record<string, unknown>;
  created_at: string;
}

// ═══════════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════════

export async function listBusinesses(includeInactive = false): Promise<Business[]> {
  return djangoFetch(`/api/v1/admin/businesses/?include_inactive=${includeInactive}`);
}
export async function getBusiness(id: string): Promise<Business> {
  return djangoFetch(`/api/v1/admin/businesses/${id}`);
}
export async function createBusiness(data: BusinessIn): Promise<Business> {
  return djangoFetch('/api/v1/admin/businesses/', { method: 'POST', body: JSON.stringify(data) });
}
export async function updateBusiness(id: string, data: BusinessUpdate): Promise<Business> {
  return djangoFetch(`/api/v1/admin/businesses/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}
export async function deleteBusiness(id: string): Promise<void> {
  await djangoFetch(`/api/v1/admin/businesses/${id}`, { method: 'DELETE' });
}
export async function uploadBusinessLogo(id: string, file: File): Promise<Business> {
  const jwt = await getJwt();
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${DJANGO_PROXY}/api/v1/admin/businesses/${id}/logo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.detail || `Upload failed (${res.status})`, res.status);
  }
  return res.json();
}
export async function getAvailableMetaAccounts(id: string): Promise<AvailableMetaAccounts> {
  return djangoFetch(`/api/v1/admin/businesses/${id}/available-meta-accounts`);
}
export async function connectMetaAccounts(id: string, data: SelectMetaAccountsIn): Promise<MetaAccountsOut> {
  return djangoFetch(`/api/v1/admin/businesses/${id}/meta-accounts`, { method: 'POST', body: JSON.stringify(data) });
}
export async function disconnectMetaAccounts(id: string): Promise<void> {
  await djangoFetch(`/api/v1/admin/businesses/${id}/meta-accounts`, { method: 'DELETE' });
}
export async function getContentConfig(id: string): Promise<ContentConfig> {
  return djangoFetch(`/api/v1/admin/businesses/${id}/content-config`);
}
export async function updateContentConfig(id: string, data: ContentConfigIn): Promise<ContentConfig> {
  return djangoFetch(`/api/v1/admin/businesses/${id}/content-config`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function getBusinessEvents(id: string, limit = 50): Promise<BusinessEvent[]> {
  return djangoFetch(`/api/v1/admin/businesses/${id}/events?limit=${limit}`);
}

// ── Drafts + auto-posting ─────────────────────────────────────

export interface BusinessDraftMedia {
  kind: string;
  media_url: string;
  thumbnail_url: string;
  alt_text: string;
}

export interface BusinessDraft {
  id: number;
  platform: string;
  post_type: string;
  caption: string;
  hashtags: string[];
  ai_generated: boolean;
  created_at: string;
  updated_at: string;
  media: BusinessDraftMedia[];
}

export async function listBusinessDrafts(id: string, limit = 50): Promise<BusinessDraft[]> {
  return djangoFetch(`/api/v1/admin/businesses/${id}/drafts?limit=${limit}`);
}

export async function approveDraft(
  businessId: string, postId: number, opts: { scheduled_at?: string } = {},
): Promise<{ post_id: number; status: string; scheduled_at: string; detail: string }> {
  const qs = opts.scheduled_at
    ? `?scheduled_at=${encodeURIComponent(opts.scheduled_at)}`
    : '';
  return djangoFetch(
    `/api/v1/admin/businesses/${businessId}/drafts/${postId}/approve${qs}`,
    { method: 'POST', body: '{}' },
  );
}

export async function approveAllDrafts(
  businessId: string,
  opts: { scheduled_at?: string; stagger_minutes?: number } = {},
): Promise<{
  approved_count: number;
  base_scheduled_at: string;
  posts: { post_id: number; scheduled_at: string }[];
}> {
  const qs = new URLSearchParams();
  if (opts.scheduled_at) qs.set('scheduled_at', opts.scheduled_at);
  if (opts.stagger_minutes) qs.set('stagger_minutes', String(opts.stagger_minutes));
  const suffix = qs.toString();
  return djangoFetch(
    `/api/v1/admin/businesses/${businessId}/drafts/approve-all${suffix ? '?' + suffix : ''}`,
    { method: 'POST', body: '{}' },
  );
}

export async function rejectDraft(businessId: string, postId: number): Promise<void> {
  await djangoFetch(
    `/api/v1/admin/businesses/${businessId}/drafts/${postId}/reject`,
    { method: 'POST', body: '{}' },
  );
}

export async function triggerGenerateContent(
  businessId: string, opts: { custom_instructions?: string; force?: boolean } = {},
): Promise<{ business_id: string; task_id: string; detail: string }> {
  const qs = new URLSearchParams();
  if (opts.custom_instructions) qs.set('custom_instructions', opts.custom_instructions);
  if (opts.force) qs.set('force', 'true');
  const suffix = qs.toString();
  return djangoFetch(
    `/api/v1/admin/businesses/${businessId}/generate-content${suffix ? '?' + suffix : ''}`,
    { method: 'POST', body: '{}' },
  );
}

// ── Catalog ───────────────────────────────────────────────────

export interface BusinessCatalogItem {
  id: string;
  name: string;
  description: string;
  category: string;
  day_of_week: string;
  image_url: string;
  extra_images: string[];
  times_used: number;
  last_used_at: string | null;
  is_active: boolean;
}

export async function listCatalog(id: string): Promise<BusinessCatalogItem[]> {
  return djangoFetch(`/api/v1/admin/businesses/${id}/catalog`);
}

// ── Config version history ────────────────────────────────────

export interface ContentConfigVersion {
  id: string;
  version: number;
  actor: string;
  data: Record<string, unknown>;
  created_at: string;
}

export async function listContentConfigVersions(
  businessId: string, limit = 20,
): Promise<ContentConfigVersion[]> {
  return djangoFetch(
    `/api/v1/admin/businesses/${businessId}/content-config/versions?limit=${limit}`,
  );
}

export async function createCatalogItem(
  id: string,
  data: { name: string; image_url?: string; description?: string; category?: string; day_of_week?: string },
): Promise<{ id: string; name: string }> {
  const qs = new URLSearchParams(data as Record<string, string>);
  return djangoFetch(
    `/api/v1/admin/businesses/${id}/catalog?${qs.toString()}`,
    { method: 'POST', body: '{}' },
  );
}

export async function deleteCatalogItem(businessId: string, itemId: string): Promise<void> {
  await djangoFetch(
    `/api/v1/admin/businesses/${businessId}/catalog/${itemId}`, { method: 'DELETE' },
  );
}

// ── AI Agent config (Company-level) ───────────────────────────

export interface AIAgentConfig {
  ai_agent_name: string;
  ai_preferred_model: string;
  /** URL de la foto/avatar del agente que se muestra en el chat del dashboard.
   *  Vacío = avatar por defecto. Per-org (Company.ai_agent_photo). */
  ai_agent_photo?: string;
}

export async function getAIAgentConfig(): Promise<AIAgentConfig> {
  return djangoFetch('/api/v1/admin/company/ai-agent-config');
}

export async function updateAIAgentConfig(data: Partial<AIAgentConfig>): Promise<AIAgentConfig> {
  return djangoFetch('/api/v1/admin/company/ai-agent-config', {
    method: 'PUT', body: JSON.stringify(data),
  });
}

// ── Marketing safety gates (Company-level) ────────────────────

export interface MarketingSafety {
  marketing_live_enabled: boolean;
  ai_optimizer_enabled: boolean;
}

export async function getMarketingSafety(): Promise<MarketingSafety> {
  return djangoFetch('/api/v1/admin/company/marketing-safety');
}

export async function updateMarketingSafety(
  data: Partial<MarketingSafety>,
): Promise<MarketingSafety> {
  return djangoFetch('/api/v1/admin/company/marketing-safety', {
    method: 'PUT', body: JSON.stringify(data),
  });
}
